import React, { useEffect, useState } from 'react';
import { Grid, Typography, CircularProgress } from '@material-ui/core';
import TextInput from '../TextInput';
import Button from '../Button';
import api from '../../services/api';
import Snackbar from '../../util/SnackBar';

export const CAPSULA_OPTIONS = [
  'MAIN COLLECTION',
  'PRE FALL',
  'FOOTWEAR',
  'SWIMWEAR',
  'TAKE AWAY',
  'HOLIDAY',
  'PRE SPRING',
  'SKI',
  'FARM ETC',
  'MARINA BITU',
  'VALENTINES',
];

export const STYLE_COLLECTION_OPTIONS = ['MAIN', 'SWIMWEAR', 'FOOTWEAR'];

export const STATUS_OPTIONS = ['Available', 'Cancelled'];

export const DELIVERY_LABEL_OPTIONS = ['1', '2', '3'];

export const DELIVERY_LABEL_TO_ID = {
  '1': '5f34a6b5ed5ff21fbc9939c6',
  '2': '5f34a7b6bc82e351349c76bf',
  '3': '5f34a7c2bc82e351349c76c0',
};

export const DELIVERY_ID_TO_LABEL = Object.entries(DELIVERY_LABEL_TO_ID).reduce(
  (acc, [label, id]) => {
    acc[id] = label;
    return acc;
  },
  {}
);

const emptyForm = {
  capsula: '',
  style_collection: '',
  status: '',
  deliveryLabel: '',
  color: '',
  msr_price_usd: '',
  wholesale_usd: '',
  msr_price_eur: '',
  wholesale_eur: '',
  msr_price_lib: '',
  wholesale_lib: '',
  description: '',
  composition: '',
  order_by_delivery: '',
  print_name: '',
  sustainableTags: '',
};

const toFormState = data => ({
  capsula: data.capsula || '',
  style_collection: data.style_collection || '',
  status: data.status || '',
  deliveryLabel: DELIVERY_ID_TO_LABEL[data.delivery_id] || '',
  color: data.color || '',
  msr_price_usd: data.msr_price_usd || '',
  wholesale_usd: data.wholesale_usd || '',
  msr_price_eur: data.msr_price_eur || '',
  wholesale_eur: data.wholesale_eur || '',
  msr_price_lib: data.msr_price_lib || '',
  wholesale_lib: data.wholesale_lib || '',
  description: data.description || '',
  composition: data.composition || '',
  order_by_delivery:
    data.order_by_delivery !== undefined && data.order_by_delivery !== null
      ? String(data.order_by_delivery)
      : '',
  print_name: Array.isArray(data.print_name) ? data.print_name[0] || '' : '',
  sustainableTags: Array.isArray(data.sustainableTags)
    ? data.sustainableTags[0] || ''
    : '',
});

export default function EditProductForm({ styleCode, onSaved }) {
  const [product, setProduct] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    let cancelled = false;
    const fetchProduct = async () => {
      if (!styleCode) return;
      setLoading(true);
      try {
        const { data } = await api.get(`/product/${styleCode}`);
        if (cancelled) return;
        if (!data || !data.style_code) {
          Snackbar.error('Product not found');
          setProduct(null);
          setForm(emptyForm);
          return;
        }
        setProduct(data);
        setForm(toFormState(data));
      } catch (err) {
        if (!cancelled) Snackbar.error('Failed to fetch product');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProduct();
    return () => {
      cancelled = true;
    };
  }, [styleCode]);

  const validate = () => {
    if (!form.capsula) return 'Capsule is required';
    if (!form.style_collection) return 'Style collection is required';
    if (!form.status) return 'Status is required';
    if (!form.deliveryLabel) return 'Delivery is required';
    if (!form.print_name.trim()) return 'Print name is required';
    return null;
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      Snackbar.warning(error);
      return;
    }
    setSaving(true);
    try {
      const orderByDelivery = form.order_by_delivery.trim();
      const body = [
        {
          ...product,
          capsula: form.capsula,
          style_collection: form.style_collection,
          status: form.status,
          delivery_id: DELIVERY_LABEL_TO_ID[form.deliveryLabel],
          color: form.color,
          msr_price_usd: form.msr_price_usd,
          wholesale_usd: form.wholesale_usd,
          msr_price_eur: form.msr_price_eur,
          wholesale_eur: form.wholesale_eur,
          msr_price_lib: form.msr_price_lib,
          wholesale_lib: form.wholesale_lib,
          description: form.description,
          composition: form.composition,
          order_by_delivery:
            orderByDelivery === '' ? null : Number(orderByDelivery),
          print_name: form.print_name.trim() ? [form.print_name.trim()] : [],
          sustainableTags: form.sustainableTags.trim()
            ? [form.sustainableTags.trim()]
            : [],
        },
      ];
      await api.put('/products', body);
      Snackbar.success('Product updated');
      const { data } = await api.get(`/product/${product.style_code}`);
      if (data && data.style_code) {
        setProduct(data);
        setForm(toFormState(data));
      }
      if (onSaved) onSaved();
    } catch (err) {
      Snackbar.error('Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        style={{ minHeight: 360 }}
      >
        <CircularProgress style={{ color: '#000' }} />
      </Grid>
    );
  }

  if (!product) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        style={{ minHeight: 200 }}
      >
        <Typography>Product not found.</Typography>
      </Grid>
    );
  }

  const formatDate = iso => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch (err) {
      return null;
    }
  };

  const lastUpdatedAt = formatDate(product.updatedAt);
  const lastUpdatedBy = product.last_updated_by;
  const hasUpdateLog = lastUpdatedBy || lastUpdatedAt;

  return (
    <Grid container style={{ padding: '0 24px' }}>
      {hasUpdateLog && (
        <Grid
          container
          alignItems="center"
          style={{
            width: '100%',
            marginBottom: 16,
            padding: '8px 12px',
            background: '#f5f5f5',
            borderLeft: '3px solid #000',
            fontSize: 12,
            color: '#5b5b5b',
            gap: 8,
          }}
        >
          <span>
            Last updated{lastUpdatedBy ? ' by ' : ''}
            {lastUpdatedBy && (
              <span style={{ fontWeight: 600, color: '#000' }}>
                {lastUpdatedBy}
              </span>
            )}
            {lastUpdatedAt ? ` · ${lastUpdatedAt}` : ''}
          </span>
        </Grid>
      )}

      <Grid container wrap="nowrap" alignItems="flex-start">
        {product.mainly_image && (
          <Grid style={{ marginRight: 24, flexShrink: 0 }}>
            <Typography
              style={{ color: '#5b5b5b', fontSize: 12, lineHeight: '34px' }}
            >
              Preview
            </Typography>
            <img
              src={product.mainly_image}
              alt={product.style_code}
              style={{
                width: 171,
                height: 245,
                objectFit: 'cover',
                background: '#f5f5f5',
              }}
            />
          </Grid>
        )}
        <Grid container>
          <TextInput
            field="Style Code"
            value={product.style_code}
            height={43}
            width={220}
            marginRight={30}
            disabled
          />
          <TextInput
            field="Color"
            value={form.color}
            onChange={e => setField('color', e.target.value)}
            height={43}
            width={300}
          />
        </Grid>
      </Grid>

      <Grid container style={{ marginTop: 24 }}>
        <TextInput
          select
          field="Capsule"
          options={CAPSULA_OPTIONS}
          value={form.capsula}
          onChange={e => setField('capsula', e.target.value)}
          height={40}
          width={260}
          marginRight={20}
          background="#ebebeb"
        />
        <TextInput
          select
          field="Style Collection"
          options={STYLE_COLLECTION_OPTIONS}
          value={form.style_collection}
          onChange={e => setField('style_collection', e.target.value)}
          height={40}
          width={220}
          marginRight={20}
          background="#ebebeb"
        />
        <TextInput
          select
          field="Status"
          options={STATUS_OPTIONS}
          value={form.status}
          onChange={e => setField('status', e.target.value)}
          height={40}
          width={180}
          marginRight={20}
          background="#ebebeb"
        />
        <TextInput
          select
          field="Delivery"
          options={DELIVERY_LABEL_OPTIONS}
          value={form.deliveryLabel}
          onChange={e => setField('deliveryLabel', e.target.value)}
          height={40}
          width={120}
          background="#ebebeb"
        />
      </Grid>

      <Grid container style={{ marginTop: 24 }}>
        <TextInput
          field="MSR Price USD"
          value={form.msr_price_usd}
          onChange={e => setField('msr_price_usd', e.target.value)}
          height={40}
          width={180}
          marginRight={20}
        />
        <TextInput
          field="Wholesale USD"
          value={form.wholesale_usd}
          onChange={e => setField('wholesale_usd', e.target.value)}
          height={40}
          width={180}
          marginRight={20}
        />
        <TextInput
          field="MSR Price EUR"
          value={form.msr_price_eur}
          onChange={e => setField('msr_price_eur', e.target.value)}
          height={40}
          width={180}
          marginRight={20}
        />
        <TextInput
          field="Wholesale EUR"
          value={form.wholesale_eur}
          onChange={e => setField('wholesale_eur', e.target.value)}
          height={40}
          width={180}
        />
      </Grid>

      <Grid container style={{ marginTop: 24 }}>
        <TextInput
          field="MSR Price LIB"
          value={form.msr_price_lib}
          onChange={e => setField('msr_price_lib', e.target.value)}
          height={40}
          width={180}
          marginRight={20}
        />
        <TextInput
          field="Wholesale LIB"
          value={form.wholesale_lib}
          onChange={e => setField('wholesale_lib', e.target.value)}
          height={40}
          width={180}
        />
      </Grid>

      <Grid container style={{ marginTop: 24 }}>
        <TextInput
          field="Order by Delivery"
          type="number"
          value={form.order_by_delivery}
          onChange={e => setField('order_by_delivery', e.target.value)}
          height={40}
          width={200}
        />
      </Grid>

      <Grid container style={{ marginTop: 24 }}>
        <TextInput
          field="Description"
          value={form.description}
          onChange={e => setField('description', e.target.value)}
          height={40}
          width={620}
          marginRight={20}
        />
        <TextInput
          field="Composition"
          value={form.composition}
          onChange={e => setField('composition', e.target.value)}
          height={40}
          width={300}
        />
      </Grid>

      <Grid container style={{ marginTop: 24 }}>
        <TextInput
          field="Print Name"
          value={form.print_name}
          onChange={e => setField('print_name', e.target.value)}
          height={40}
          width={460}
          marginRight={20}
        />
        <TextInput
          field="Sustainable Tag (optional)"
          value={form.sustainableTags}
          onChange={e => setField('sustainableTags', e.target.value)}
          height={40}
          width={460}
        />
      </Grid>

      <Grid container style={{ marginTop: 32 }}>
        <Button
          text="Save Changes"
          color="#000"
          textColor="#fff"
          width={268}
          height={40}
          loading={saving}
          onClick={handleSave}
        />
      </Grid>
    </Grid>
  );
}
